import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { format, addDays, startOfWeek, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isSameMonth, getDaysInMonth, getDay } from 'date-fns';
import { roomsService, bookingsService, availabilityService, availabilityBlocksService, addonsService, coursesService, paymentsService } from '../services/api';
import { toast } from 'react-toastify';
import StaffReservationSidebar from './StaffReservationSidebar';
import AvailabilityBlockManager from './AvailabilityBlockManager';
import DebugAvailabilityCalendar from './DebugAvailabilityCalendar';
import EditBookingModal from './modals/EditBookingModal';
import { sortRoomsByType } from '../utils/roomTypeSequence';
import { useIsMobile } from '../hooks';
import { Eye as EyeIcon, Globe as GlobeIcon, Package as PackageIcon, Pencil as PencilIcon, Trash2 as Trash2Icon, XCircle as XCircleIcon } from 'lucide-react';
import { usePmsDataRefresh } from '../agent/dataSync';

// One room's calendar response in the shape the grid renders. Shared by the
// full load and by the in-place refresh after the PMS agent changes something.
function toCalendarBookings(calendarData, room) {
  return (calendarData.bookings || []).map(booking => {
    const transformed = {
      id: booking.id,
      bookingId: booking.bookingId || booking.id, // Add bookingId for edit modal
      startDate: booking.checkIn || booking.startDate,
      endDate: booking.checkOut || booking.endDate,
      checkIn: booking.checkIn || booking.startDate, // Add both formats for compatibility
      checkOut: booking.checkOut || booking.endDate,
      guestName: booking.userName || booking.user?.name || 'Guest', // Fallback for public API
      guestEmail: booking.userEmail || booking.user?.email || '', // Fallback for public API
      status: booking.status || 'PENDING',
      paymentStatus: booking.paymentStatus || 'PENDING',
      totalPrice: booking.totalPrice || 0,
      bedNumber: booking.bedNumber,
      bedId: booking.bedId,
      user: booking.user || {
        name: booking.userName || 'Guest',
        email: booking.userEmail || '',
        role: booking.userRole || 'GUEST'
      },
      course: booking.course,
      payments: booking.payments || [],
      type: 'booking'
    };

    // Log booking dates for debugging long bookings
    if (transformed.startDate && transformed.endDate) {
      const days = Math.ceil((new Date(transformed.endDate) - new Date(transformed.startDate)) / (1000 * 60 * 60 * 24));
      if (days > 30) {
        console.log(`Long booking detected for room ${room.roomNumber}:`, {
          bookingId: transformed.id,
          guestName: transformed.guestName,
          startDate: transformed.startDate,
          endDate: transformed.endDate,
          days: days,
          status: transformed.status
        });
      }
    }

    return transformed;
  });
}

function toCalendarBlocks(calendarData) {
  return (calendarData.availabilityBlocks || []).map(block => ({
    id: block.id,
    name: block.name || 'Availability Block',
    description: block.description,
    startDate: block.startDate,
    endDate: block.endDate,
    blockType: block.blockType || 'MAINTENANCE',
    bedNumber: block.bedNumber,
    bedId: block.bedId, // Add bedId for bed-specific blocks
    isActive: block.isActive !== false, // Default to true unless explicitly false
    type: 'block'
  }));
}

function AvailabilityCalendar() {
  const isMobile = useIsMobile();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'week' or 'month'
  const [rooms, setRooms] = useState([]);
  const [roomBookings, setRoomBookings] = useState({});
  const [roomBlocks, setRoomBlocks] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showEditBookingModal, setShowEditBookingModal] = useState(false);
  const [showBlockManager, setShowBlockManager] = useState(false);
  const [showStaffReservationSidebar, setShowStaffReservationSidebar] = useState(false);
  const [showDebugMode, setShowDebugMode] = useState(false);
  
  // Block edit state
  const [showEditBlockModal, setShowEditBlockModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  
  // State for pre-filled block data when creating from empty cell
  const [prefilledBlockData, setPrefilledBlockData] = useState(null);
  
  // New state for booking action menu
  const [showBookingActionMenu, setShowBookingActionMenu] = useState(false);
  const [actionMenuPosition, setActionMenuPosition] = useState({ x: 0, y: 0 });
  const [actionMenuBooking, setActionMenuBooking] = useState(null);
  
  // Check if current user can create reservations (staff/admin)
  const [canCreateReservations, setCanCreateReservations] = useState(false);
  const [user, setUser] = useState(null);
  
  // Bulk selection mode state
  const [bulkSelectionMode, setBulkSelectionMode] = useState(false);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  
  // Ref to prevent duplicate API calls
  const loadingInProgress = useRef(false);
  const lastLoadedRange = useRef(null);

  const monthLabelColumnWidth = isMobile ? 160 : 200;
  const monthDayColumnWidth = isMobile ? 44 : 40;
  const weekRoomColumnWidth = isMobile ? 160 : 200;
  const weekBedColumnWidth = isMobile ? 150 : 180;
  const weekDayColumnWidth = isMobile ? 90 : null;
  const calendarScrollStyle = {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: '8px'
  };

  useEffect(() => {
    // Check if we have a valid token, don't overwrite existing one
    const existingToken = localStorage.getItem('token');
    if (!existingToken) {
      console.warn('No authentication token found. Please login.');
      toast.error('Authentication required. Please login to view calendar.');
      setLoading(false);
      return;
    }

    // Verify token is not expired
    try {
      const payload = JSON.parse(atob(existingToken.split('.')[1]));
      const now = Date.now() / 1000;
      
      if (payload.exp < now) {
        console.warn('Token expired');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error('Session expired. Please login again.');
        setLoading(false);
        return;
      }
      
      console.log('Token is valid for user:', payload.email);
    } catch (error) {
      console.error('Invalid token format:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      toast.error('Invalid authentication token. Please login again.');
      setLoading(false);
      return;
    }
    
    // Calculate date range for this view
    const startDate = viewMode === 'week' ? 
      startOfWeek(currentWeek, { weekStartsOn: 1 }) : 
      startOfMonth(currentMonth);
    const endDate = viewMode === 'week' ? 
      addDays(startDate, 6) : 
      endOfMonth(currentMonth);
    
    // Create a unique key for this date range
    const rangeKey = `${viewMode}-${startDate.toISOString()}-${endDate.toISOString()}`;
    
    // Skip if already loading or if we just loaded this exact range
    if (loadingInProgress.current) {
      console.log('Skipping duplicate load - already in progress');
      return;
    }
    
    if (lastLoadedRange.current === rangeKey) {
      console.log('Skipping duplicate load - same range already loaded:', rangeKey);
      return;
    }
    
    // Mark the range we're about to load
    lastLoadedRange.current = rangeKey;
    loadRoomsAndBookings();
    checkUserPermissions();
  }, [currentWeek, currentMonth, viewMode]);

  const checkUserPermissions = () => {
    // Check if user has staff or admin role
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');
    
    // If no user data but we have a token, try to decode user info from token
    let userRole = userData.role || userData.Role || userData.userRole || userData.type;
    
    if (!userRole && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userRole = payload.role || payload.Role;
        console.log('Extracted role from token:', userRole);
      } catch (error) {
        console.error('Failed to decode token for role:', error);
      }
    }
    
    // Set user data with role
    const userWithRole = { ...userData, role: userRole };
    setUser(userWithRole);
    
    // Check for staff/admin permissions
    const hasPermission = ['STAFF', 'PROPERTYADMIN', 'SUPERADMIN', 'ADMIN'].includes(userRole?.toUpperCase());
    
    console.log('Permission check:', { 
      user: userWithRole, 
      role: userRole, 
      hasPermission: hasPermission,
      token: !!token,
      localStorage: localStorage.getItem('user')
    });
    
    setCanCreateReservations(hasPermission); // Fixed: was !hasPermission
  };

  const handleReservationCreated = () => {
    // Reset the last loaded range to force a reload
    lastLoadedRange.current = null;
    // Reload the calendar data when a new reservation is created
    loadRoomsAndBookings();
    toast.success('Reservation created successfully!');
  };

  // silent: refresh without the loading screen or toasts (used after agent changes)
  const loadRoomsAndBookings = async ({ silent = false } = {}) => {
    // Prevent duplicate concurrent calls
    if (loadingInProgress.current) {
      console.log('loadRoomsAndBookings: Already loading, skipping duplicate call');
      return;
    }
    
    // Check authentication before making API calls
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token found');
      toast.error('Authentication required. Please login to view calendar.');
      setLoading(false);
      return;
    }

    try {
      // Mark loading as in progress
      loadingInProgress.current = true;
      if (!silent) setLoading(true);
      
      // Debug: Check user authentication
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('AvailabilityCalendar Debug - User:', user);
      console.log('AvailabilityCalendar Debug - Token exists:', !!token);
      console.log('AvailabilityCalendar Debug - User role:', user.role);
      
      // Load all rooms first - Fix pagination to get all rooms
      console.log('Step 1: Loading rooms...');
      const roomsResponse = await roomsService.getAll({ limit: 100, page: 1 });
      let roomsData = roomsResponse.data?.rooms || roomsResponse.rooms || roomsResponse || [];
      console.log(`Loaded ${roomsData.length} rooms (total: ${roomsResponse.data?.total || 'unknown'})`);
      
      // Sort rooms by room type priority sequence
      roomsData = sortRoomsByType(roomsData);
      console.log('Rooms sorted by room type sequence');
      
      setRooms(roomsData);

      if (roomsData.length === 0) {
        console.warn('No rooms found in the system');
        toast.warning('No rooms found in the system');
        setLoading(false);
        return;
      }

      // Load booking data and availability blocks for each room
      const bookingsData = {};
      const blocksData = {};
      let totalBookings = 0;
      let totalBlocks = 0;
      
      // Calculate date range for calendar data
      const startDate = viewMode === 'week' ? 
        startOfWeek(currentWeek, { weekStartsOn: 1 }) : 
        startOfMonth(currentMonth);
      const endDate = viewMode === 'week' ? 
        addDays(startDate, 6) : 
        endOfMonth(currentMonth);
      
      console.log('Loading calendar data for date range:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        viewMode
      });
      
      console.log('Step 2: Loading calendar data for each room...');
      for (const room of roomsData) {
        try {
          console.log(`Loading calendar data for room ${room.roomNumber} (${room.id})...`);
          
          // Load unified calendar data (bookings + blocks)
          const calendarResponse = await availabilityService.getRoomCalendar(room.id, {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          });
          
          console.log(`Calendar response for room ${room.roomNumber}:`, {
            success: calendarResponse.success,
            bookingsCount: calendarResponse.data?.bookings?.length || 0,
            blocksCount: calendarResponse.data?.availabilityBlocks?.length || 0
          });
          
          if (calendarResponse.success && calendarResponse.data) {
            const calendarData = calendarResponse.data;
            
            // Transform bookings to the expected format
            const transformedBookings = toCalendarBookings(calendarData, room);

            bookingsData[room.id] = transformedBookings;
            totalBookings += transformedBookings.length;

            // Transform blocks to the expected format
            const transformedBlocks = toCalendarBlocks(calendarData);

            blocksData[room.id] = transformedBlocks;
            totalBlocks += transformedBlocks.length;
            
            console.log(`Room ${room.roomNumber} data processed:`, {
              bookings: transformedBookings.length,
              blocks: transformedBlocks.length
            });
          } else {
            console.warn(`Calendar response not successful for room ${room.roomNumber}:`, calendarResponse);
            bookingsData[room.id] = [];
            blocksData[room.id] = [];
          }
        } catch (error) {
          console.error(`Failed to load calendar data for room ${room.roomNumber}:`, error);
          console.error('Error details:', {
            message: error.message,
            status: error.status,
            response: error.response?.data
          });
          
          // Handle authentication errors
          if (error.status === 401 || error.status === 403) {
            console.error('Authentication error detected, token may be invalid');
            // Don't clear token here, let the user handle re-authentication
          }
          
          bookingsData[room.id] = [];
          blocksData[room.id] = [];
        }
      }
      
      console.log('Final calendar data loaded:', {
        totalRooms: roomsData.length,
        totalBookings,
        totalBlocks,
        roomsWithBookings: Object.values(bookingsData).filter(bookings => bookings.length > 0).length,
        roomsWithBlocks: Object.values(blocksData).filter(blocks => blocks.length > 0).length
      });
      
      setRoomBookings(bookingsData);
      setRoomBlocks(blocksData);
      
      // Show success message (not for silent refreshes after agent changes)
      if (silent) {
        // the grid updates in place
      } else if (totalBookings > 0 || totalBlocks > 0) {
        toast.success(`Loaded ${totalBookings} bookings and ${totalBlocks} blocks for ${roomsData.length} rooms`);
      } else {
        toast.info(`Loaded ${roomsData.length} rooms with no current bookings or blocks`);
      }
      
    } catch (error) {
      console.error('Critical error loading rooms and bookings:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        response: error.response?.data
      });
      
      // Handle different types of errors
      if (error.status === 401 || error.status === 403) {
        toast.error('Authentication failed. Please login again.');
        // Clear invalid auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } else if (error.status >= 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Failed to load calendar data. Please refresh the page.');
      }
      
      // Set empty data to prevent crashes
      setRooms([]);
      setRoomBookings({});
      setRoomBlocks({});
    } finally {
      setLoading(false);
      // Mark loading as complete
      loadingInProgress.current = false;
    }
  };

  // The PMS agent changed availability (blocks, check-ins, check-outs): re-read
  // only the affected rooms and update them in place, without the loading screen.
  const refreshAfterAgentChange = async (change) => {
    if (loadingInProgress.current) {
      // A full load is running and may have started before the change; load again quietly after it.
      setTimeout(() => {
        lastLoadedRange.current = null;
        loadRoomsAndBookings({ silent: true });
      }, 1500);
      return;
    }
    const roomsById = new Map(rooms.map((room) => [room.id, room]));
    const targets = (change.roomIds || []).map((id) => roomsById.get(id)).filter(Boolean);
    if (targets.length === 0 || targets.length > 15) {
      await loadRoomsAndBookings({ silent: true });
      return;
    }
    const rangeStart = viewMode === 'week' ? startOfWeek(currentWeek, { weekStartsOn: 1 }) : startOfMonth(currentMonth);
    const rangeEnd = viewMode === 'week' ? addDays(rangeStart, 6) : endOfMonth(currentMonth);
    const fresh = (
      await Promise.all(
        targets.map(async (room) => {
          try {
            const response = await availabilityService.getRoomCalendar(room.id, {
              startDate: rangeStart.toISOString(),
              endDate: rangeEnd.toISOString()
            });
            return response.success && response.data ? { room, data: response.data } : null;
          } catch (error) {
            console.error(`Refreshing room ${room.roomNumber} after an agent change failed:`, error);
            return null;
          }
        })
      )
    ).filter(Boolean);
    if (fresh.length === 0) return;
    setRoomBookings((current) => {
      const next = { ...current };
      fresh.forEach(({ room, data }) => { next[room.id] = toCalendarBookings(data, room); });
      return next;
    });
    setRoomBlocks((current) => {
      const next = { ...current };
      fresh.forEach(({ room, data }) => { next[room.id] = toCalendarBlocks(data); });
      return next;
    });
  };

  usePmsDataRefresh(['availability'], refreshAfterAgentChange);

  const getWeekDays = () => {
    const start = startOfWeek(currentWeek, { weekStartsOn: 1 }); // Start on Monday
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  const getMonthDays = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const daysInMonth = getDaysInMonth(currentMonth);
    const startDay = getDay(start);
    const adjustedStartDay = startDay === 0 ? 6 : startDay - 1; // Convert Sunday (0) to 6, Monday (1) to 0, etc.
    
    const days = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < adjustedStartDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
    }
    
    return days;
  };

  const nextWeek = () => {
    setCurrentWeek(prevWeek => addWeeks(prevWeek, 1));
  };

  const prevWeek = () => {
    setCurrentWeek(prevWeek => subWeeks(prevWeek, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(prevMonth => addMonths(prevMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(prevMonth => subMonths(prevMonth, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentWeek(today);
    setCurrentMonth(today);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Generate month options for the selector (-12 to +12 months)
  const generateMonthOptions = () => {
    const today = new Date();
    const options = [];
    
    for (let i = -12; i <= 12; i++) {
      const monthDate = addMonths(today, i);
      options.push({
        value: monthDate.toISOString(),
        label: format(monthDate, 'MMMM yyyy'),
        date: monthDate
      });
    }
    
    return options;
  };

  const handleMonthSelect = (e) => {
    const selectedDate = new Date(e.target.value);
    setCurrentMonth(selectedDate);
    if (viewMode === 'week') {
      setCurrentWeek(selectedDate);
    }
  };

  // Separate rooms into private and shared
  const privateRooms = rooms.filter(room => !room.roomType?.isShared);
  const sharedRooms = rooms.filter(room => room.roomType?.isShared);

  const isRoomBookedOnDate = (roomId, date) => {
    const bookings = roomBookings[roomId] || [];
    console.log(`Checking room ${roomId} bookings for ${date.toDateString()}:`, bookings);
    
    return bookings.find(booking => {
      // Try multiple date property names for compatibility
      const checkInDate = booking.startDate || booking.checkIn;
      const checkOutDate = booking.endDate || booking.checkOut;
      
      if (!checkInDate || !checkOutDate) {
        console.warn(`Booking ${booking.id} missing dates:`, { checkInDate, checkOutDate, booking });
        return false;
      }
      
      const checkIn = new Date(checkInDate);
      const checkOut = new Date(checkOutDate);
      
      // Check for invalid dates
      if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
        console.warn(`Invalid dates for booking ${booking.id}:`, { checkIn: checkInDate, checkOut: checkOutDate });
        return false;
      }
      
      // Normalize dates to avoid timezone issues
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const checkInOnly = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
      const checkOutOnly = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
      
      // Check if date is within booking range (excluding checkout date - room is available on checkout day)
      const isBooked = dateOnly >= checkInOnly && dateOnly < checkOutOnly;
      
      if (isBooked) {
        console.log(`Found booking for room ${roomId} on ${date.toDateString()}:`, {
          bookingId: booking.id,
          guestName: booking.guestName,
          checkIn: checkInOnly.toDateString(),
          checkOut: checkOutOnly.toDateString(),
          status: booking.status
        });
      }
      
      return isBooked;
    });
  };

  const isBedBookedOnDate = (roomId, bedNumber, date) => {
    const bookings = roomBookings[roomId] || [];
    
    return bookings.find(booking => {
      if (!booking.bedNumber || booking.bedNumber !== bedNumber) return false;
      
      // Try multiple date property names for compatibility
      const checkInDate = booking.startDate || booking.checkIn;
      const checkOutDate = booking.endDate || booking.checkOut;
      
      if (!checkInDate || !checkOutDate) {
        console.warn(`Bed booking ${booking.id} missing dates:`, { checkInDate, checkOutDate, booking });
        return false;
      }
      
      const checkIn = new Date(checkInDate);
      const checkOut = new Date(checkOutDate);
      
      // Check for invalid dates
      if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
        console.warn(`Invalid dates for bed booking ${booking.id}:`, { checkIn: checkInDate, checkOut: checkOutDate });
        return false;
      }
      
      // Normalize dates to avoid timezone issues
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const checkInOnly = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
      const checkOutOnly = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
      
      // Check if date is within booking range (excluding checkout date - room is available on checkout day)
      const isBooked = dateOnly >= checkInOnly && dateOnly < checkOutOnly;
      
      if (isBooked) {
        console.log(`Found bed booking for room ${roomId}, bed ${bedNumber} on ${date.toDateString()}:`, {
          bookingId: booking.id,
          bedNumber: bedNumber,
          guestName: booking.guestName,
          checkIn: checkInOnly.toDateString(),
          checkOut: checkOutOnly.toDateString(),
          status: booking.status
        });
      }
      
      return isBooked;
    });
  };

  // Updated function to check if room is blocked on a specific date
  const isRoomBlockedOnDate = (roomId, date) => {
    const blocks = roomBlocks[roomId] || [];
    
    return blocks.find(block => {
      if (!block.isActive) return false;
      
      const blockStart = new Date(block.startDate);
      const blockEnd = new Date(block.endDate);
      
      // Normalize dates to avoid timezone issues
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const blockStartOnly = new Date(blockStart.getFullYear(), blockStart.getMonth(), blockStart.getDate());
      const blockEndOnly = new Date(blockEnd.getFullYear(), blockEnd.getMonth(), blockEnd.getDate());
      
      const isBlocked = dateOnly >= blockStartOnly && dateOnly <= blockEndOnly;
      
      if (isBlocked) {
        console.log(`Found room block for room ${roomId} on ${date.toDateString()}:`, block);
      }
      
      return isBlocked;
    });
  };

  // Updated function to check if bed is blocked on a specific date
  const isBedBlockedOnDate = (roomId, bedNumber, date) => {
    const blocks = roomBlocks[roomId] || [];
    return blocks.find(block => {
      if (!block.isActive) return false;
      
      // Check if this block applies to the specific bed
      // If block has no bedNumber/bedId, it applies to the whole room
      // If block has bedNumber, it only applies to that specific bed
      if (block.bedNumber && block.bedNumber !== bedNumber) return false;
      
      const blockStart = new Date(block.startDate);
      const blockEnd = new Date(block.endDate);
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const blockStartOnly = new Date(blockStart.getFullYear(), blockStart.getMonth(), blockStart.getDate());
      const blockEndOnly = new Date(blockEnd.getFullYear(), blockEnd.getMonth(), blockEnd.getDate());
      
      return dateOnly >= blockStartOnly && dateOnly <= blockEndOnly;
    });
  };

  // Updated room status function to include blocking
  const getRoomStatusOnDate = (roomId, date) => {
    const booking = isRoomBookedOnDate(roomId, date);
    const block = isRoomBlockedOnDate(roomId, date);
    
    // Debug logging
    if (booking || block) {
      console.log(`Room ${roomId} status on ${date.toDateString()}:`, {
        booking: booking ? { id: booking.id, guest: booking.guestName, status: booking.status } : null,
        block: block ? { id: block.id, name: block.name, type: block.blockType } : null
      });
    }
    
    if (block) {
      return {
        status: 'blocked',
        booking: null,
        block: block,
        color: '#975534', // Purple for blocks
        textColor: 'white'
      };
    }
    
    if (booking) {
      return {
        status: 'booked',
        booking: booking,
        block: null,
        color: '#A13F2F', // Red for bookings
        textColor: 'white'
      };
    }
    
    return {
      status: 'available',
      booking: null,
      block: null,
      color: '#1A3E2A', // Green for available
      textColor: 'white'
    };
  };

  // Updated bed status function to include blocking
  const getBedStatusOnDate = (roomId, bedNumber, date) => {
    const booking = isBedBookedOnDate(roomId, bedNumber, date);
    const block = isBedBlockedOnDate(roomId, bedNumber, date);
    
    if (block) {
      return {
        status: 'blocked',
        booking: null,
        block: block,
        color: '#975534', // Purple for blocks
        textColor: 'white'
      };
    }
    
    if (booking) {
      return {
        status: 'booked',
        booking: booking,
        block: null,
        color: '#A13F2F', // Red for bookings
        textColor: 'white'
      };
    }
    
    return {
      status: 'available',
      booking: null,
      block: null,
      color: '#1A3E2A', // Green for available
      textColor: 'white'
    };
  };

  // Updated click handler to show action menu for bookings
  const handleRoomClick = (room, date, booking, block, event, bed = null) => {
    console.log('Room click handler called with:', {
      room: room ? { id: room.id, roomNumber: room.roomNumber } : null,
      bed: bed ? { id: bed.id, bedNumber: bed.bedNumber, bedType: bed.bedType } : null,
      date: date ? date.toISOString() : null,
      booking: booking,
      block: block,
      bulkSelectionMode: bulkSelectionMode
    });
    
    if (booking) {
      // For bookings, show action menu at click position (not affected by bulk selection mode)
      const rect = event?.target?.getBoundingClientRect();
      const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
      const y = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
      
      setActionMenuPosition({ x, y });
      setActionMenuBooking({
        ...booking,
        room: room,
        bed: bed,
        date: date,
        type: 'booking'
      });
      setShowBookingActionMenu(true);
    } else if (block) {
      // For blocks, check if in bulk selection mode
      if (bulkSelectionMode) {
        // Toggle block selection
        setSelectedBlocks(prevSelected => {
          const blockId = block.id;
          if (prevSelected.includes(blockId)) {
            // Remove from selection
            return prevSelected.filter(id => id !== blockId);
          } else {
            // Add to selection
            return [...prevSelected, blockId];
          }
        });
      } else {
        // Normal mode - show details modal
        const selectedData = {
          ...block,
          room: room,
          bed: bed,
          date: date,
          type: 'block'
        };
        console.log('Setting selected block data:', selectedData);
        setSelectedBooking(selectedData);
        setShowBookingModal(true);
      }
    }
  };

  const closeBookingActionMenu = () => {
    setShowBookingActionMenu(false);
    setActionMenuBooking(null);
  };

  const handleViewBookingDetails = () => {
    if (actionMenuBooking) {
      setSelectedBooking(actionMenuBooking);
      setShowBookingModal(true);
      closeBookingActionMenu();
    }
  };

  const handleEditBookingFromMenu = () => {
    console.log('handleEditBookingFromMenu called with actionMenuBooking:', actionMenuBooking);
    if (actionMenuBooking) {
      console.log('Calling openEditBookingModal with:', actionMenuBooking);
      openEditBookingModal(actionMenuBooking);
      closeBookingActionMenu();
    } else {
      console.error('No actionMenuBooking available');
    }
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedBooking(null);
  };

  const openEditBookingModal = async (booking) => {
    try {
      console.log('Opening edit modal for booking:', booking);
      
      // First, try to fetch the complete booking data using the booking ID
      const bookingId = booking.bookingId || booking.id;
      
      if (!bookingId) {
        toast.error('Cannot edit booking: Missing booking ID');
        return;
      }
      
      console.log('Fetching complete booking data for ID:', bookingId);
      
      // Fetch complete booking data from the API
      const response = await bookingsService.getById(bookingId);
      let completeBooking;
      
      if (response && response.success && response.data) {
        completeBooking = response.data;
      } else if (response && response.id) {
        completeBooking = response;
      } else {
        // Fallback to using the calendar booking data with room info
        console.warn('Could not fetch complete booking data, using calendar data');
        completeBooking = {
          ...booking,
          roomId: booking.room?.id,
          room: booking.room
        };
      }
      
      console.log('Complete booking data for edit modal:', completeBooking);
      
      console.log('Setting selectedBooking to:', completeBooking);
      console.log('Setting showEditBookingModal to true');
      setSelectedBooking(completeBooking);
      setShowBookingModal(false);
      setShowEditBookingModal(true);
      
      // Add a small delay to check if state was set
      setTimeout(() => {
        console.log('State check after setting - showEditBookingModal should be true');
      }, 100);
    } catch (error) {
      console.error('Error fetching booking for edit:', error);
      // Fallback to using the calendar booking data
      console.log('Using calendar booking data as fallback');
      const fallbackBooking = {
        ...booking,
        roomId: booking.room?.id,
        room: booking.room
      };
      console.log('Using fallback booking:', fallbackBooking);
      console.log('Setting selectedBooking to fallback');
      console.log('Setting showEditBookingModal to true (fallback)');
      setSelectedBooking(fallbackBooking);
      setShowBookingModal(false);
      setShowEditBookingModal(true);
      
      // Add a small delay to check if state was set
      setTimeout(() => {
        console.log('Fallback state check - showEditBookingModal should be true');
      }, 100);
    }
  };

  const closeEditBookingModal = () => {
    setShowEditBookingModal(false);
    setSelectedBooking(null);
    // Reset range cache and reload calendar data to show updated booking
    lastLoadedRange.current = null;
    loadRoomsAndBookings();
  };

  // Block management handlers
  const handleEditBlock = (block) => {
    console.log('Editing block:', block);
    setEditingBlock(block);
    setShowEditBlockModal(true);
    setShowBookingModal(false);
  };

  const handleDeleteBlock = async (block) => {
    if (!window.confirm(`Are you sure you want to delete the block "${block.name}"?`)) {
      return;
    }

    try {
      console.log('Deleting block:', block.id);
      await availabilityBlocksService.delete(block.id);
      toast.success(`Block "${block.name}" deleted successfully`);
      setShowBookingModal(false);
      setSelectedBooking(null);
      // Reset range cache and reload calendar data
      lastLoadedRange.current = null;
      loadRoomsAndBookings();
    } catch (error) {
      console.error('Error deleting block:', error);
      toast.error(error.message || 'Failed to delete block');
    }
  };
  
  // Bulk selection mode handlers
  const toggleBulkSelectionMode = () => {
    const newMode = !bulkSelectionMode;
    setBulkSelectionMode(newMode);
    if (!newMode) {
      // Exiting selection mode, clear selections
      setSelectedBlocks([]);
    }
    toast.info(newMode ? 'Bulk selection mode enabled. Click blocks to select them.' : 'Bulk selection mode disabled.');
  };
  
  const handleBulkDeleteBlocks = async () => {
    if (selectedBlocks.length === 0) {
      toast.warning('No blocks selected for deletion');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedBlocks.length} selected block(s)?`)) {
      return;
    }
    
    try {
      console.log('Bulk deleting blocks:', selectedBlocks);
      
      // Delete all selected blocks
      const deletePromises = selectedBlocks.map(blockId => 
        availabilityBlocksService.delete(blockId)
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`Successfully deleted ${selectedBlocks.length} block(s)`);
      
      // Clear selections and exit selection mode
      setSelectedBlocks([]);
      setBulkSelectionMode(false);
      
      // Reset range cache and reload calendar data
      lastLoadedRange.current = null;
      loadRoomsAndBookings();
    } catch (error) {
      console.error('Error bulk deleting blocks:', error);
      toast.error(error.message || 'Failed to delete some blocks');
    }
  };
  
  const cancelBulkSelection = () => {
    setSelectedBlocks([]);
    setBulkSelectionMode(false);
    toast.info('Selection cancelled');
  };

  const handleUpdateBlock = async (blockId, updateData) => {
    try {
      console.log('Updating block:', blockId, updateData);
      await availabilityBlocksService.update(blockId, updateData);
      toast.success('Block updated successfully');
      setShowEditBlockModal(false);
      setEditingBlock(null);
      // Reset range cache and reload calendar data
      lastLoadedRange.current = null;
      loadRoomsAndBookings();
    } catch (error) {
      console.error('Error updating block:', error);
      toast.error(error.message || 'Failed to update block');
      throw error;
    }
  };

  const closeEditBlockModal = () => {
    setShowEditBlockModal(false);
    setEditingBlock(null);
  };

  // Handler for clicking empty cells to create availability blocks
  const handleEmptyCellClick = (room, date, bed = null) => {
    console.log('Empty cell clicked:', {
      room: room ? { id: room.id, roomNumber: room.roomNumber } : null,
      bed: bed ? { id: bed.id, bedNumber: bed.bedNumber } : null,
      date: date ? format(date, 'yyyy-MM-dd') : null
    });
    
    // Set pre-filled data for the block manager
    setPrefilledBlockData({
      roomId: room.id,
      bedId: bed ? bed.id : null,
      startDate: format(date, 'yyyy-MM-dd'),
      blockLevel: bed ? 'bed' : 'room'
    });
    
    // Open the block manager modal
    setShowBlockManager(true);
  };

  const getWeekRange = () => {
    const weekDays = getWeekDays();
    const start = format(weekDays[0], 'MMM dd');
    const end = format(weekDays[6], 'MMM dd, yyyy');
    return `${start} - ${end}`;
  };

  const renderMonthView = () => {
    const monthDays = getMonthDays();
    
    return (
      <div style={{ marginBottom: '30px' }}>
        {/* Month Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          padding: '0 10px'
        }}>
          <h3 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#24332A',
            margin: 0
          }}>
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          
          {/* Legend for Month View */}
          <div style={{ 
            display: 'flex', 
            gap: '15px',
            alignItems: 'center',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: 'white', border: '2px solid #E3DFD3', borderRadius: '2px' }}></div>
              <span>Available</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#1A3E2A', borderRadius: '2px' }}></div>
              <span>Confirmed</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#B68235', borderRadius: '2px' }}></div>
              <span>Pending</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#D48A7C', borderRadius: '2px' }}></div>
              <span>Checked In</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#772D21', borderRadius: '2px' }}></div>
              <span>Checked Out</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#A9613C', borderRadius: '2px' }}></div>
              <span>Blocked</span>
            </div>
          </div>
        </div>

        {/* Room-wise Month Calendar */}
        <div style={calendarScrollStyle}>
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #E3DFD3',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            minWidth: isMobile ? `${monthLabelColumnWidth + (monthDays.length * monthDayColumnWidth)}px` : 'auto'
          }}>
          {/* Calendar Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? `${monthLabelColumnWidth}px repeat(${monthDays.length}, ${monthDayColumnWidth}px)`
              : '200px repeat(auto-fit, minmax(40px, 1fr))',
            backgroundColor: '#F5F3EB',
            borderBottom: '2px solid #E3DFD3'
          }}>
            <div style={{
              padding: '12px 16px',
              fontWeight: '700',
              fontSize: '14px',
              color: '#36433B',
              borderRight: '1px solid #E3DFD3'
            }}>
              Room / Bed
            </div>
            
            {/* Date Headers */}
            {monthDays.map((day, index) => {
              if (!day) {
                return (
                  <div 
                    key={`empty-header-${index}`}
                    style={{
                      padding: '8px 4px',
                      minWidth: '40px',
                      borderRight: '1px solid #EEEBE1'
                    }}
                  />
                );
              }
              
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              
              return (
                <div 
                  key={day.toISOString()}
                  style={{
                    padding: '8px 4px',
                    textAlign: 'center',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: isToday(day) ? '#1A3E2A' : isWeekend ? '#A13F2F' : '#36433B',
                    backgroundColor: isToday(day) ? '#E1E9E2' : 'transparent',
                    borderRight: '1px solid #EEEBE1',
                    minWidth: '40px'
                  }}
                >
                  <div style={{ fontSize: '10px', marginBottom: '2px' }}>
                    {format(day, 'EEE')}
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: '700' }}>
                    {format(day, 'dd')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Room Rows */}
          {rooms.map((room, roomIndex) => (
            <React.Fragment key={room.id}>
              {/* Private Room Row */}
              {!room.roomType?.isShared ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile
                    ? `${monthLabelColumnWidth}px repeat(${monthDays.length}, ${monthDayColumnWidth}px)`
                    : '200px repeat(auto-fit, minmax(40px, 1fr))',
                  borderBottom: '1px solid #EEEBE1',
                  backgroundColor: roomIndex % 2 === 0 ? '#F5F3EB' : 'white'
                }}>
                  {/* Room Info */}
                  <div style={{
                    padding: '12px 16px',
                    borderRight: '1px solid #E3DFD3',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '13px', 
                      color: '#24332A',
                      marginBottom: '2px'
                    }}>
                      Room {room.roomNumber}
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#5F6B61'
                    }}>
                      {room.roomType?.name || 'Private'} • ₹{room.roomType?.defaultRate || 0}
                    </div>
                  </div>

                  {/* Date Cells */}
                  {monthDays.map((day, dayIndex) => {
                    if (!day) {
                      return (
                        <div 
                          key={`empty-${roomIndex}-${dayIndex}`}
                          style={{
                            minHeight: '45px',
                            borderRight: '1px solid #EEEBE1',
                            minWidth: '40px'
                          }}
                        />
                      );
                    }

                    const roomStatus = getRoomStatusOnDate(room.id, day);
                    const booking = roomStatus.booking;
                    const block = roomStatus.block;
                    
                    let cellColor = 'white';
                    let cellText = '';
                    let textColor = '#5F6B61';
                    
                    if (block) {
                      cellColor = '#A9613C';
                      cellText = 'BLOCK';
                      textColor = 'white';
                    } else if (booking) {
                      if (booking.status === 'CONFIRMED') {
                        cellColor = '#1A3E2A';
                        cellText = booking.guestName?.split(' ')[0]?.substring(0, 6) || 'GUEST';
                        textColor = 'white';
                      } else if (booking.status === 'PENDING') {
                        cellColor = '#B68235';
                        cellText = booking.guestName?.split(' ')[0]?.substring(0, 6) || 'PENDING';
                        textColor = 'white';
                      } else if (booking.status === 'CHECKEDIN') {
                        cellColor = '#D48A7C'; // Light red for checked in
                        cellText = booking.guestName?.split(' ')[0]?.substring(0, 6) || 'IN';
                        textColor = 'white';
                      } else if (booking.status === 'CHECKEDOUT') {
                        cellColor = '#772D21'; // Dark red for checked out
                        cellText = booking.guestName?.split(' ')[0]?.substring(0, 6) || 'OUT';
                        textColor = 'white';
                      }
                    }

                    return (
                      <div 
                        key={`${room.id}-${dayIndex}`}
                        onClick={(e) => {
                          if (booking || block) {
                            handleRoomClick(room, day, booking, block, e);
                          } else {
                            // Empty cell - open block creation modal with pre-filled data
                            handleEmptyCellClick(room, day);
                          }
                        }}
                        style={{
                          minHeight: '45px',
                          backgroundColor: cellColor,
                          borderRight: '1px solid #EEEBE1',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '4px 2px',
                          cursor: 'pointer', // Always show pointer cursor
                          transition: 'all 0.2s ease',
                          minWidth: '40px',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (booking || block) {
                            e.target.style.transform = 'scale(1.05)';
                            e.target.style.zIndex = '10';
                            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                          } else {
                            // Subtle hover effect for empty cells
                            e.target.style.backgroundColor = '#EEEBE1';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (booking || block) {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.zIndex = '1';
                            e.target.style.boxShadow = 'none';
                          } else {
                            // Reset empty cell background
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        {/* Date indicator at top */}
                        <div style={{
                          fontSize: '10.5px',
                          fontWeight: '700',
                          lineHeight: '1',
                          color: '#000000'
                        }}>
                          {format(day, 'dd')}
                        </div>
                        
                        {/* Main content */}
                        {cellText && (
                          <span style={{
                            fontSize: '9px',
                            fontWeight: '700',
                            color: textColor,
                            textAlign: 'center',
                            lineHeight: '1.1',
                            wordBreak: 'break-all',
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {cellText}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Shared Room - Show each bed as a separate row
                room.beds?.map((bed, bedIndex) => (
                  <div 
                    key={`${room.id}-${bed.id}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile
                        ? `${monthLabelColumnWidth}px repeat(${monthDays.length}, ${monthDayColumnWidth}px)`
                        : '200px repeat(auto-fit, minmax(40px, 1fr))',
                      borderBottom: '1px solid #EEEBE1',
                      backgroundColor: (roomIndex + bedIndex) % 2 === 0 ? '#F5F3EB' : 'white'
                    }}
                  >
                    {/* Bed Info */}
                    <div style={{
                      padding: '12px 16px',
                      borderRight: '1px solid #E3DFD3',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <div style={{ 
                        fontWeight: '600', 
                        fontSize: '13px', 
                        color: '#24332A',
                        marginBottom: '2px'
                      }}>
                        Room {room.roomNumber} - Bed {bed.bedNumber}
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#5F6B61',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        {room.roomType?.name || 'Shared'} • ₹{room.roomType?.defaultRate || 0}
                        {bed.isFemaleOnly && (
                          <span style={{
                            backgroundColor: '#F8F0EA',
                            color: '#A9613C',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            fontSize: '9px',
                            fontWeight: '600'
                          }}>
                            F
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Date Cells for Bed */}
                    {monthDays.map((day, dayIndex) => {
                      if (!day) {
                        return (
                          <div 
                            key={`empty-bed-${bed.id}-${dayIndex}`}
                            style={{
                              minHeight: '45px',
                              borderRight: '1px solid #EEEBE1',
                              minWidth: '40px'
                            }}
                          />
                        );
                      }

                      const bedStatus = getBedStatusOnDate(room.id, bed.bedNumber, day);
                      const booking = bedStatus.booking;
                      const block = bedStatus.block;
                      
                      let cellColor = 'white';
                      let cellText = '';
                      let textColor = '#5F6B61';
                      
                      if (block) {
                        cellColor = '#A9613C';
                        cellText = 'BLOCK';
                        textColor = 'white';
                      } else if (booking) {
                        if (booking.status === 'CONFIRMED') {
                          cellColor = '#1A3E2A';
                          cellText = booking.guestName?.split(' ')[0]?.substring(0, 6) || 'GUEST';
                          textColor = 'white';
                        } else if (booking.status === 'PENDING') {
                          cellColor = '#B68235';
                          cellText = booking.guestName?.split(' ')[0]?.substring(0, 6) || 'PENDING';
                          textColor = 'white';
                        } else if (booking.status === 'CHECKEDIN') {
                          cellColor = '#D48A7C'; // Light red for checked in
                          cellText = booking.guestName?.split(' ')[0]?.substring(0, 6) || 'IN';
                          textColor = 'white';
                        } else if (booking.status === 'CHECKEDOUT') {
                          cellColor = '#772D21'; // Dark red for checked out
                          cellText = booking.guestName?.split(' ')[0]?.substring(0, 6) || 'OUT';
                          textColor = 'white';
                        }
                      }

                      return (
                        <div 
                          key={`${bed.id}-${dayIndex}`}
                          onClick={(e) => {
                            if (booking || block) {
                              handleRoomClick(room, day, booking, block, e, bed);
                            } else {
                              // Empty cell - open block creation modal with pre-filled data
                              handleEmptyCellClick(room, day, bed);
                            }
                          }}
                          style={{
                            minHeight: '45px',
                            backgroundColor: cellColor,
                            borderRight: '1px solid #EEEBE1',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '4px 2px',
                            cursor: 'pointer', // Always show pointer cursor
                            transition: 'all 0.2s ease',
                            minWidth: '40px',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (booking || block) {
                              e.target.style.transform = 'scale(1.05)';
                              e.target.style.zIndex = '10';
                              e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                            } else {
                              // Subtle hover effect for empty cells
                              e.target.style.backgroundColor = '#EEEBE1';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (booking || block) {
                              e.target.style.transform = 'scale(1)';
                              e.target.style.zIndex = '1';
                              e.target.style.boxShadow = 'none';
                            } else {
                              // Reset empty cell background
                              e.target.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          {/* Date indicator at top */}
                          <div style={{
                            fontSize: '10.5px',
                            fontWeight: '700',
                            lineHeight: '1',
                            color: '#000000'
                          }}>
                            {format(day, 'dd')}
                          </div>
                          
                          {/* Main content */}
                          {cellText && (
                            <span style={{
                              fontSize: '9px',
                              fontWeight: '700',
                              color: textColor,
                              textAlign: 'center',
                              lineHeight: '1.1',
                              wordBreak: 'break-all',
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              gap: '2px'
                            }}>
                              {cellText}
                              {/* Booking Engine Icon */}
                              {booking && booking.bookingPlatform === 'BOOKING_ENGINE' && (
                                <span style={{
                                  fontSize: '8px',
                                  opacity: 0.9
                                }} title="Booking Engine">
                                  <GlobeIcon size="1em" aria-hidden="true" />
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </React.Fragment>
          ))}

          {/* Summary Row - Updated with detailed occupancy */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? `${monthLabelColumnWidth}px repeat(${monthDays.length}, ${monthDayColumnWidth}px)`
              : '200px repeat(auto-fit, minmax(40px, 1fr))',
            backgroundColor: '#EEEBE1',
            borderTop: '2px solid #D2CDBF'
          }}>
            <div style={{
              padding: '12px 16px',
              fontWeight: '700',
              fontSize: '14px',
              color: '#36433B',
              borderRight: '1px solid #E3DFD3',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <div>Occupancy Summary</div>
              <div style={{ fontSize: '10px', fontWeight: '500', color: '#5F6B61' }}>
                (excl. maintenance)
              </div>
            </div>
            
            {monthDays.map((day, index) => {
              if (!day) {
                return (
                  <div 
                    key={`summary-empty-${index}`}
                    style={{
                      minHeight: '70px',
                      borderRight: '1px solid #EEEBE1',
                      minWidth: '40px'
                    }}
                  />
                );
              }

              const occupancy = getRoomOccupancyForDay(day);
              const overallPercent = occupancy.overallRoomPercent;

              return (
                <div 
                  key={`summary-${index}`}
                  style={{
                    minHeight: '70px',
                    backgroundColor: overallPercent > 80 ? '#F9EEEB' : overallPercent > 50 ? '#F3E6CB' : '#F0F3EE',
                    borderRight: '1px solid #EEEBE1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '40px',
                    padding: '4px 2px',
                    gap: '3px'
                  }}
                  title={`Private: ${occupancy.privateOccupied}/${occupancy.privateTotal} (${occupancy.privatePercent}%)\nShared Rooms: ${occupancy.sharedRoomsOccupied}/${occupancy.sharedRoomsTotal} (${occupancy.sharedRoomsPercent}%)\nShared Beds: ${occupancy.sharedOccupied}/${occupancy.sharedTotal} (${occupancy.sharedPercent}%)`}
                >
                  {/* Overall Room Occupancy */}
                  <div style={{ 
                    fontSize: '13px', 
                    fontWeight: '800',
                    color: overallPercent > 80 ? '#8E3628' : overallPercent > 50 ? '#9E712E' : '#163626'
                  }}>
                    {overallPercent}%
                  </div>
                  
                  {/* Private Rooms */}
                  {occupancy.privateTotal > 0 && (
                    <div style={{ 
                      fontSize: '8px',
                      color: '#36433B',
                      fontWeight: '600',
                      textAlign: 'center'
                    }}>
                      P: {occupancy.privateOccupied}/{occupancy.privateTotal}
                    </div>
                  )}
                  
                  {/* Shared Rooms */}
                  {occupancy.sharedRoomsTotal > 0 && (
                    <div style={{ 
                      fontSize: '8px',
                      color: '#36433B',
                      fontWeight: '600',
                      textAlign: 'center'
                    }}>
                      S: {occupancy.sharedRoomsOccupied}/{occupancy.sharedRoomsTotal}
                    </div>
                  )}
                  
                  {/* Shared Beds Detail */}
                  {occupancy.sharedTotal > 0 && (
                    <div style={{ 
                      fontSize: '7px',
                      color: '#5F6B61',
                      fontWeight: '500',
                      textAlign: 'center'
                    }}>
                      ({occupancy.sharedOccupied}/{occupancy.sharedTotal}b)
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Month Statistics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '16px',
          marginTop: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#122E20' }}>
              {getTotalRoomsAvailable()}
            </div>
            <div style={{ fontSize: '12px', color: '#122E20', fontWeight: '600' }}>
              Total Rooms
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#9E712E' }}>
              {getTotalBedsAvailable()}
            </div>
            <div style={{ fontSize: '12px', color: '#9E712E', fontWeight: '600' }}>
              Total Beds
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#163626' }}>
              {getMonthConfirmedBookingCount()}
            </div>
            <div style={{ fontSize: '12px', color: '#163626', fontWeight: '600' }}>
              Confirmed Bookings
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#9E712E' }}>
              {getMonthPendingBookingCount()}
            </div>
            <div style={{ fontSize: '12px', color: '#9E712E', fontWeight: '600' }}>
              Pending Bookings
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#D48A7C' }}>
              {getMonthCheckedInBookingCount()}
            </div>
            <div style={{ fontSize: '12px', color: '#D48A7C', fontWeight: '600' }}>
              Checked In
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#772D21' }}>
              {getMonthCheckedOutBookingCount()}
            </div>
            <div style={{ fontSize: '12px', color: '#772D21', fontWeight: '600' }}>
              Checked Out
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#A9613C' }}>
              {getMonthBlockCount()}
            </div>
            <div style={{ fontSize: '12px', color: '#A9613C', fontWeight: '600' }}>
              Active Blocks
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#24332A' }}>
              {Math.round(getMonthAverageOccupancy())}%
            </div>
            <div style={{ fontSize: '12px', color: '#24332A', fontWeight: '600' }}>
              Avg Room Occupancy
            </div>
            <div style={{ fontSize: '10px', color: '#5F6B61', marginTop: '4px' }}>
              (excl. maintenance)
            </div>
          </div>
          
          {/* NEW: Private Rooms Occupancy */}
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#1A3E2A' }}>
              {(() => {
                const monthDays = getMonthDays().filter(day => day !== null);
                let totalPercent = 0;
                monthDays.forEach(day => {
                  const occ = getRoomOccupancyForDay(day);
                  totalPercent += occ.privatePercent;
                });
                return Math.round(totalPercent / monthDays.length);
              })()}%
            </div>
            <div style={{ fontSize: '12px', color: '#1A3E2A', fontWeight: '600' }}>
              Private Rooms Avg
            </div>
          </div>
          
          {/* NEW: Shared Rooms Occupancy */}
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#1A3E2A' }}>
              {(() => {
                const monthDays = getMonthDays().filter(day => day !== null);
                let totalPercent = 0;
                monthDays.forEach(day => {
                  const occ = getRoomOccupancyForDay(day);
                  totalPercent += occ.sharedRoomsPercent;
                });
                return Math.round(totalPercent / monthDays.length);
              })()}%
            </div>
            <div style={{ fontSize: '12px', color: '#1A3E2A', fontWeight: '600' }}>
              Shared Rooms Avg
            </div>
          </div>
          
          {/* NEW: Shared Beds Detail */}
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #E3DFD3',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '800', color: '#163626' }}>
              {(() => {
                const monthDays = getMonthDays().filter(day => day !== null);
                let totalPercent = 0;
                monthDays.forEach(day => {
                  const occ = getRoomOccupancyForDay(day);
                  totalPercent += occ.sharedPercent;
                });
                return Math.round(totalPercent / monthDays.length);
              })()}%
            </div>
            <div style={{ fontSize: '12px', color: '#163626', fontWeight: '600' }}>
              Shared Beds Avg
            </div>
          </div>
          </div>
        </div>
      </div>
    );
  };

  const getTotalAvailableForDay = (day) => {
    let available = 0;
    rooms.forEach(room => {
      if (!room.roomType?.isShared) {
        // Private room
        const isBooked = isRoomBookedOnDate(room.id, day);
        const isBlocked = isRoomBlockedOnDate(room.id, day);
        if (!isBooked && !isBlocked) {
          available += 1;
        }
      } else {
        // Shared room - count available beds
        room.beds?.forEach(bed => {
          const isBedBooked = isBedBookedOnDate(room.id, bed.bedNumber, day);
          const isBedBlocked = isBedBlockedOnDate(room.id, bed.bedNumber, day);
          if (!isBedBooked && !isBedBlocked) {
            available += 1;
          }
        });
      }
    });
    return available;
  };

  // Calculate room occupancy based on bookings only (blocks do not affect occupancy)
  const getRoomOccupancyForDay = (day) => {
    const privateRooms = rooms.filter(room => !room.roomType?.isShared);
    const sharedRooms = rooms.filter(room => room.roomType?.isShared);
    
    let privateOccupied = 0;
    let privateTotal = privateRooms.length;
    let sharedOccupied = 0;
    let sharedTotal = 0;
    
    // Calculate private room occupancy
    privateRooms.forEach(room => {
      if (isRoomBookedOnDate(room.id, day)) {
        privateOccupied += 1;
      }
    });
    
    // Calculate shared room occupancy (bed-level)
    sharedRooms.forEach(room => {
      room.beds?.forEach(bed => {
        sharedTotal += 1;
        if (isBedBookedOnDate(room.id, bed.bedNumber, day)) {
          sharedOccupied += 1;
        }
      });
    });
    
    // Calculate room-level occupancy for shared rooms
    let sharedRoomsOccupied = 0;
    let sharedRoomsTotal = 0;
    sharedRooms.forEach(room => {
      const totalBeds = room.beds?.length || 0;
      if (totalBeds === 0) return;
      
      sharedRoomsTotal += 1;
      
      let occupiedBeds = 0;
      room.beds?.forEach(bed => {
        if (isBedBookedOnDate(room.id, bed.bedNumber, day)) {
          occupiedBeds += 1;
        }
      });
      
      // Room is considered occupied if any bed is booked
      if (occupiedBeds > 0) {
        sharedRoomsOccupied += 1;
      }
    });
    
    return {
      privateOccupied,
      privateTotal,
      privatePercent: privateTotal > 0 ? Math.round((privateOccupied / privateTotal) * 100) : 0,
      sharedOccupied,
      sharedTotal,
      sharedPercent: sharedTotal > 0 ? Math.round((sharedOccupied / sharedTotal) * 100) : 0,
      sharedRoomsOccupied,
      sharedRoomsTotal,
      sharedRoomsPercent: sharedRoomsTotal > 0 ? Math.round((sharedRoomsOccupied / sharedRoomsTotal) * 100) : 0,
      totalRooms: privateTotal + sharedRoomsTotal,
      occupiedRooms: privateOccupied + sharedRoomsOccupied,
      overallRoomPercent: (privateTotal + sharedRoomsTotal) > 0 
        ? Math.round(((privateOccupied + sharedRoomsOccupied) / (privateTotal + sharedRoomsTotal)) * 100) 
        : 0
    };
  };

  const getTotalRoomsAvailable = () => {
    return rooms.length;
  };

  const getTotalBedsAvailable = () => {
    return rooms.reduce((total, room) => total + (room.beds?.length || 0), 0);
  };

  const getMonthBookingCount = () => {
    let count = 0;
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    Object.values(roomBookings).forEach(bookings => {
      bookings.forEach(booking => {
        const bookingStart = new Date(booking.checkIn);
        const bookingEnd = new Date(booking.checkOut);
        if (bookingStart <= monthEnd && bookingEnd >= monthStart) {
          count++;
        }
      });
    });
    
    return count;
  };

  const getMonthBlockCount = () => {
    let count = 0;
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    Object.values(roomBlocks).forEach(blocks => {
      blocks.forEach(block => {
        if (!block.isActive) return;
        const blockStart = new Date(block.startDate);
        const blockEnd = new Date(block.endDate);
        if (blockStart <= monthEnd && blockEnd >= monthStart) {
          count++;
        }
      });
    });
    
    return count;
  };

  const getTotalRoomsAndBedsCount = () => {
    let count = 0;
    rooms.forEach(room => {
      if (!room.roomType?.isShared) {
        count += 1; // Private room counts as 1
      } else {
        count += room.beds?.length || 0; // Shared room counts beds
      }
    });
    return count;
  };

  const getMonthConfirmedBookingCount = () => {
    let count = 0;
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    Object.values(roomBookings).forEach(bookings => {
      bookings.forEach(booking => {
        if (booking.status === 'CONFIRMED') {
          const bookingStart = new Date(booking.checkIn);
          const bookingEnd = new Date(booking.checkOut);
          if (bookingStart <= monthEnd && bookingEnd >= monthStart) {
            count++;
          }
        }
      });
    });
    
    return count;
  };

  const getMonthPendingBookingCount = () => {
    let count = 0;
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    Object.values(roomBookings).forEach(bookings => {
      bookings.forEach(booking => {
        if (booking.status === 'PENDING') {
          const bookingStart = new Date(booking.checkIn);
          const bookingEnd = new Date(booking.checkOut);
          if (bookingStart <= monthEnd && bookingEnd >= monthStart) {
            count++;
          }
        }
      });
    });
    
    return count;
  };

  const getMonthCheckedInBookingCount = () => {
    let count = 0;
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    Object.values(roomBookings).forEach(bookings => {
      bookings.forEach(booking => {
        if (booking.status === 'CHECKEDIN') {
          const bookingStart = new Date(booking.checkIn);
          const bookingEnd = new Date(booking.checkOut);
          if (bookingStart <= monthEnd && bookingEnd >= monthStart) {
            count++;
          }
        }
      });
    });
    
    return count;
  };

  const getMonthCheckedOutBookingCount = () => {
    let count = 0;
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    Object.values(roomBookings).forEach(bookings => {
      bookings.forEach(booking => {
        if (booking.status === 'CHECKEDOUT') {
          const bookingStart = new Date(booking.checkIn);
          const bookingEnd = new Date(booking.checkOut);
          if (bookingStart <= monthEnd && bookingEnd >= monthStart) {
            count++;
          }
        }
      });
    });
    
    return count;
  };

  const getMonthAverageOccupancy = () => {
    const monthDays = getMonthDays().filter(day => day !== null);
    
    if (monthDays.length === 0) return 0;
    
    let totalRoomOccupancyPercent = 0;
    monthDays.forEach(day => {
      const occupancy = getRoomOccupancyForDay(day);
      totalRoomOccupancyPercent += occupancy.overallRoomPercent;
    });
    
    return totalRoomOccupancyPercent / monthDays.length;
  };

  const showDayDetails = (day, bookings, blocks) => {
    // Create a combined view of bookings and blocks for the day
    setSelectedBooking({
      type: 'day-summary',
      date: day,
      bookings,
      blocks,
      dayName: format(day, 'EEEE, MMMM dd, yyyy')
    });
    setShowBookingModal(true);
  };

  // Debug component toggle
  const toggleDebugMode = () => {
    setShowDebugMode(!showDebugMode);
  };

  // Show debug mode if toggled
  if (showDebugMode) {
    return <DebugAvailabilityCalendar />;
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '18px'
      }}>
        <div>Loading availability calendar...</div>
      </div>
    );
  }

  const weekDays = getWeekDays();

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', maxWidth: '100%', overflowX: 'hidden', overflowY: 'auto' }}>
      {/* Updated Header with View Toggle */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '800', color: '#24332A' }}>
            Availability Calendar 
          </h2>
          <p style={{ margin: 0, color: '#5F6B61', fontSize: '16px' }}>
            {viewMode === 'week' ? 'Week view showing room and bed availability' : 'Month overview with availability summary'}
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Staff Reservation Button - Only show for authorized users */}
          {!canCreateReservations && (
            <button
              onClick={() => setShowStaffReservationSidebar(true)}
              style={{
                backgroundColor: '#163626',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            >
              Create Reservation
            </button>
          )}

          {/* View Mode Toggle */}
          <button
            onClick={() => setViewMode(prevMode => prevMode === 'week' ? 'month' : 'week')}
            style={{
              backgroundColor: viewMode === 'month' ? '#1A3E2A' : '#A9613C',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {viewMode === 'month' ? 'Week View' : 'Month View'}
          </button>

          {/* Block Manager Button */}
          <button
            onClick={() => setShowBlockManager(true)}
            style={{
              backgroundColor: '#975534',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            Manage Blocks
          </button>
          
          {/* Bulk Selection Mode Button */}
          {!bulkSelectionMode ? (
            <button
              onClick={toggleBulkSelectionMode}
              style={{
                backgroundColor: '#B68235',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Bulk Select
            </button>
          ) : (
            <>
              {/* Delete Selected Button */}
              <button
                onClick={handleBulkDeleteBlocks}
                disabled={selectedBlocks.length === 0}
                style={{
                  backgroundColor: selectedBlocks.length === 0 ? '#A5A89B' : '#A13F2F',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: selectedBlocks.length === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                Delete Selected ({selectedBlocks.length})
              </button>
              
              {/* Cancel Selection Button */}
              <button
                onClick={cancelBulkSelection}
                style={{
                  backgroundColor: '#5F6B61',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                Cancel
              </button>
            </>
          )}

          {/* Debug Mode Button */}
          <button
            onClick={toggleDebugMode}
            style={{
              backgroundColor: '#5F6B61',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            Debug Mode
          </button>
          
          {/* Navigation Buttons */}
          <button
            onClick={viewMode === 'week' ? prevWeek : prevMonth}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            ← Previous
          </button>
          
          <button
            onClick={goToToday}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            Today
          </button>

          {/* Month Selector Dropdown */}
          <select
            value={currentMonth.toISOString()}
            onChange={handleMonthSelect}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              outline: 'none'
            }}
          >
            {generateMonthOptions().map((option) => (
              <option 
                key={option.value} 
                value={option.value}
                style={{
                  backgroundColor: 'white',
                  color: '#24332A'
                }}
              >
                {option.label}
              </option>
            ))}
          </select>
          
          <button
            onClick={viewMode === 'week' ? nextWeek : nextMonth}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '12px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Week Range / Month Title */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '30px',
        fontSize: '20px',
        fontWeight: '700',
        color: '#36433B'
      }}>
        {viewMode === 'week' ? getWeekRange() : format(currentMonth, 'MMMM yyyy')}
      </div>
      
      {/* Bulk Selection Mode Banner */}
      {bulkSelectionMode && (
        <div style={{
          backgroundColor: '#F3E6CB',
          border: '3px solid #B68235',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px',
          boxShadow: '0 4px 12px rgba(182, 130, 53, 0.3)',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <div style={{
            fontSize: '32px'
          }}>
            <PackageIcon size="1em" aria-hidden="true" />
          </div>
          <div>
            <div style={{
              fontSize: '18px',
              fontWeight: '800',
              color: '#684A1F',
              marginBottom: '4px'
            }}>
              Bulk Selection Mode Active
            </div>
            <div style={{
              fontSize: '14px',
              color: '#4F3818'
            }}>
              Click on availability blocks to select/deselect them. {selectedBlocks.length > 0 ? `${selectedBlocks.length} block(s) selected.` : 'No blocks selected yet.'}
            </div>
          </div>
        </div>
      )}

      {/* Conditional Rendering Based on View Mode */}
      {viewMode === 'month' ? (
        renderMonthView()
      ) : (
        <>
          {/* Legend */}
          <div style={{ 
            display: 'flex', 
            gap: '30px', 
            marginBottom: '30px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            backgroundColor: '#F5F3EB',
            borderRadius: '12px',
            border: '1px solid #E3DFD3'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#1A3E2A', 
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '16px', fontWeight: '600' }}>Available</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#A13F2F', 
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '16px', fontWeight: '600' }}>Booked</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#975534', 
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '16px', fontWeight: '600' }}>Blocked</span>
            </div>
          </div>

          {/* Week Statistics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#F5F3EB',
            borderRadius: '12px',
            border: '1px solid #E3DFD3'
          }}>
            <div style={{
              backgroundColor: '#F0F3EE',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #1A3E2A',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#163626' }}>
                {privateRooms.length}
              </div>
              <div style={{ fontSize: '14px', color: '#163626', fontWeight: '600' }}>
                Private Rooms
              </div>
            </div>

            <div style={{
              backgroundColor: '#F3E6CB',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #B68235',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#9E712E' }}>
                {sharedRooms.length}
              </div>
              <div style={{ fontSize: '14px', color: '#9E712E', fontWeight: '600' }}>
                Shared Rooms
              </div>
            </div>

            <div style={{
              backgroundColor: '#F0F3EE',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #1A3E2A',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#163626' }}>
                {sharedRooms.reduce((total, room) => total + (room.beds?.length || 0), 0)}
              </div>
              <div style={{ fontSize: '14px', color: '#163626', fontWeight: '600' }}>
                Total Beds
              </div>
            </div>
            
            <div style={{
              backgroundColor: '#F0F3EE',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #1A3E2A',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#163626' }}>
                {weekDays.reduce((total, day) => {
                  return total + privateRooms.filter(room => 
                    getRoomStatusOnDate(room.id, day).status === 'available'
                  ).length + sharedRooms.reduce((bedTotal, room) => {
                    return bedTotal + (room.beds || []).filter(bed => 
                      getBedStatusOnDate(room.id, bed.bedNumber, day).status === 'available'
                    ).length;
                  }, 0);
                }, 0)}
              </div>
              <div style={{ fontSize: '14px', color: '#163626', fontWeight: '600' }}>
                Available This Week
              </div>
            </div>
            
            <div style={{
              backgroundColor: '#F9EEEB',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #A13F2F',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#8E3628' }}>
                {weekDays.reduce((total, day) => {
                  return total + privateRooms.filter(room => 
                    getRoomStatusOnDate(room.id, day).status === 'booked'
                  ).length + sharedRooms.reduce((bedTotal, room) => {
                    return bedTotal + (room.beds || []).filter(bed => 
                      getBedStatusOnDate(room.id, bed.bedNumber, day).status === 'booked'
                    ).length;
                  }, 0);
                }, 0)}
              </div>
              <div style={{ fontSize: '14px', color: '#8E3628', fontWeight: '600' }}>
                Booked This Week
              </div>
            </div>

            <div style={{
              backgroundColor: '#F1E0D4',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #975534',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#975534' }}>
                {weekDays.reduce((total, day) => {
                  return total + privateRooms.filter(room => 
                    getRoomStatusOnDate(room.id, day).status === 'blocked'
                  ).length + sharedRooms.reduce((bedTotal, room) => {
                    return bedTotal + (room.beds || []).filter(bed => 
                      getBedStatusOnDate(room.id, bed.bedNumber, day).status === 'blocked'
                    ).length;
                  }, 0);
                }, 0)}
              </div>
              <div style={{ fontSize: '14px', color: '#975534', fontWeight: '600' }}>
                Blocked This Week
              </div>
            </div>
          </div>

          {/* Private Rooms Calendar */}
          {privateRooms.length > 0 && (
            <div style={{ marginBottom: '50px' }}>
              <h3 style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: '#24332A', 
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                Private Rooms ({privateRooms.length})
              </h3>
              
              <div style={calendarScrollStyle}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile
                    ? `${weekRoomColumnWidth}px repeat(7, ${weekDayColumnWidth}px)`
                    : '200px repeat(7, 1fr)',
                  gap: '2px',
                  backgroundColor: '#E3DFD3',
                  padding: '2px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  minWidth: isMobile ? `${weekRoomColumnWidth + (7 * weekDayColumnWidth)}px` : 'auto'
                }}>
                {/* Header Row */}
                <div style={{ 
                  backgroundColor: '#36433B', 
                  color: 'white', 
                  padding: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '14px'
                }}>
                  Private Rooms
                </div>
                
                {weekDays.map((day, index) => (
                  <div 
                    key={index}
                    style={{ 
                      backgroundColor: isToday(day) ? '#1A3E2A' : '#36433B', 
                      color: 'white', 
                      padding: '15px 10px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '12px',
                      minHeight: '60px'
                    }}
                  >
                    <div style={{ fontSize: '14px', marginBottom: '2px' }}>
                      {format(day, 'EEE')}
                    </div>
                    <div style={{ fontSize: '16px' }}>
                      {format(day, 'dd')}
                    </div>
                    <div style={{ fontSize: '10px', opacity: 0.8 }}>
                      {format(day, 'MMM')}
                    </div>
                  </div>
                ))}

                {/* Private Room Rows */}
                {privateRooms.map((room) => (
                  <React.Fragment key={room.id}>
                    <div style={{ 
                      backgroundColor: '#F5F3EB', 
                      padding: '15px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      borderRight: '1px solid #E3DFD3'
                    }}>
                      <div style={{ 
                        fontWeight: '700', 
                        fontSize: '14px', 
                        color: '#24332A',
                        marginBottom: '3px'
                      }}>
                        Room {room.roomNumber}
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#5F6B61'
                      }}>
                        {room.roomType?.name || 'Standard'}
                      </div>
                    </div>

                    {weekDays.map((day, dayIndex) => {
                      const roomStatus = getRoomStatusOnDate(room.id, day);
                      const booking = roomStatus.booking;
                      const block = roomStatus.block;
                      const isSelected = block && selectedBlocks.includes(block.id);
                      
                      return (
                        <div 
                          key={`${room.id}-${dayIndex}`}
                          onClick={(e) => handleRoomClick(room, day, booking, block, e)}
                          style={{ 
                            backgroundColor: (isSelected && block) ? '#A5A89B' : roomStatus.color,
                            color: roomStatus.textColor,
                            padding: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: (booking || block) ? 'pointer' : 'default',
                            minHeight: '70px',
                            transition: 'background-color 0.2s ease',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (booking || block) {
                              e.target.style.transform = 'scale(1.05)';
                              e.target.style.zIndex = '10';
                              e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (booking || block) {
                              e.target.style.transform = 'scale(1)';
                              e.target.style.zIndex = '1';
                              e.target.style.boxShadow = 'none';
                            }
                          }}
                        >
                          {/* Date indicator at top */}
                          <div style={{
                            fontSize: '13.5px',
                            fontWeight: '700',
                            color: '#000000',
                            alignSelf: 'flex-start',
                            marginBottom: '2px'
                          }}>
                            {format(day, 'dd MMM')}
                          </div>

                          {/* Main content centered */}
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1
                          }}>
                            {block ? (
                              <>
                                <div style={{ 
                                  fontSize: '11px', 
                                  fontWeight: '700',
                                  textAlign: 'center',
                                  marginBottom: '2px'
                                }}>
                                  BLOCKED
                                </div>
                                <div style={{ 
                                  fontSize: '10px', 
                                  textAlign: 'center',
                                  opacity: 0.9
                                }}>
                                  {block.blockType?.replace('_', ' ')}
                                </div>
                              </>
                            ) : booking ? (
                              <>
                                <div style={{ 
                                  fontSize: '11px', 
                                  fontWeight: '700',
                                  textAlign: 'center',
                                  marginBottom: '2px'
                                }}>
                                  BOOKED
                                </div>
                                <div style={{ 
                                  fontSize: '10px', 
                                  textAlign: 'center',
                                  opacity: 0.9
                                }}>
                                  {booking.guestName?.split(' ')[0] || 'Guest'}
                                </div>
                                {/* Booking Engine Icon */}
                                {booking.bookingPlatform === 'BOOKING_ENGINE' && (
                                  <div style={{ 
                                    fontSize: '11px', 
                                    marginTop: '2px',
                                    opacity: 0.9
                                  }} title="Booking Engine">
                                    <GlobeIcon size="1em" aria-hidden="true" />
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div style={{ 
                                  fontSize: '11px', 
                                  fontWeight: '700',
                                  textAlign: 'center'
                                }}>
                                  AVAILABLE
                                </div>
                                <div style={{ 
                                  fontSize: '9px', 
                                  textAlign: 'center',
                                  opacity: 0.8,
                                  marginTop: '2px'
                                }}>
                                  ₹{room.roomType?.defaultRate || 0}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                </div>
              </div>
            </div>
          )}

          {/* Shared Rooms Calendar */}
          {sharedRooms.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: '#24332A', 
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                Shared Rooms - Bed-wise Availability ({sharedRooms.length} rooms)
              </h3>

              {sharedRooms.map((room) => (
                <div key={room.id} style={{ marginBottom: '40px' }}>
                  {/* Room Header */}
                  <div style={{
                    backgroundColor: '#F5F3EB',
                    padding: '15px 20px',
                    borderRadius: '8px 8px 0 0',
                    borderBottom: '2px solid #E3DFD3'
                  }}>
                    <h4 style={{ 
                      margin: '0 0 5px 0', 
                      fontSize: '18px', 
                      fontWeight: '700',
                      color: '#24332A'
                    }}>
                      Room {room.roomNumber} - {room.roomType?.name || 'Shared'}
                    </h4>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '14px', 
                      color: '#5F6B61' 
                    }}>
                      {room.beds?.length || 0} beds • ₹{room.roomType?.defaultRate || 0}/night per bed
                    </p>
                  </div>

                  {/* Bed Calendar Grid */}
                  <div style={calendarScrollStyle}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isMobile
                        ? `${weekBedColumnWidth}px repeat(7, ${weekDayColumnWidth}px)`
                        : '180px repeat(7, 1fr)',
                      gap: '3px',
                      backgroundColor: '#E3DFD3',
                      padding: '3px',
                      borderRadius: '0 0 12px 12px',
                      overflow: 'hidden',
                      minWidth: isMobile ? `${weekBedColumnWidth + (7 * weekDayColumnWidth)}px` : 'auto'
                    }}>
                    {/* Bed Calendar Header */}
                    <div style={{ 
                      backgroundColor: '#1A3E2A', 
                      color: 'white', 
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '13px'
                    }}>
                      Beds
                    </div>
                    
                    {weekDays.map((day, index) => (
                      <div 
                        key={index}
                        style={{ 
                          backgroundColor: isToday(day) ? '#1A3E2A' : '#1A3E2A', 
                          color: 'white', 
                          padding: '12px 8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '11px',
                          minHeight: '60px'
                        }}
                      >
                        <div style={{ fontSize: '13px', marginBottom: '2px' }}>
                          {format(day, 'EEE')}
                        </div>
                        <div style={{ fontSize: '16px' }}>
                          {format(day, 'dd')}
                        </div>
                        <div style={{ fontSize: '9px', opacity: 0.8 }}>
                          {format(day, 'MMM')}
                        </div>
                      </div>
                    ))}

                    {/* Bed Rows */}
                    {(room.beds || []).map((bed) => (
                      <React.Fragment key={bed.id}>
                        <div style={{ 
                          backgroundColor: '#EEEBE1', 
                          padding: '15px 12px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRight: '2px solid #E3DFD3'
                        }}>
                          <div style={{ 
                            fontWeight: '700', 
                            fontSize: '14px', 
                            color: '#24332A',
                            marginBottom: '3px'
                          }}>
                            Bed {bed.bedNumber}
                          </div>
                          {bed.isFemaleOnly && (
                            <div style={{ 
                              fontSize: '10px', 
                              color: '#A9613C',
                              fontWeight: '600',
                              backgroundColor: '#F8F0EA',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              alignSelf: 'flex-start'
                            }}>
                              Female Only
                            </div>
                          )}
                        </div>

                        {weekDays.map((day, dayIndex) => {
                          const bedStatus = getBedStatusOnDate(room.id, bed.bedNumber, day);
                          const booking = bedStatus.booking;
                          const block = bedStatus.block;
                          const isSelected = block && selectedBlocks.includes(block.id);
                          
                          return (
                            <div 
                              key={`${bed.id}-${dayIndex}`}
                              onClick={(e) => handleRoomClick(room, day, booking, block, e, bed)}
                              style={{ 
                                backgroundColor: (isSelected && block) ? '#A5A89B' : bedStatus.color,
                                color: bedStatus.textColor,
                                padding: '12px 8px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: (booking || block) ? 'pointer' : 'default',
                                minHeight: '85px',
                                transition: 'background-color 0.2s ease',
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                if (booking || block) {
                                  e.target.style.transform = 'scale(1.08)';
                                  e.target.style.zIndex = '10';
                                  e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (booking || block) {
                                  e.target.style.transform = 'scale(1)';
                                  e.target.style.zIndex = '1';
                                  e.target.style.boxShadow = 'none';
                                }
                              }}
                            >
                              {/* Date indicator at top */}
                              <div style={{
                                fontSize: '13.5px',
                                fontWeight: '700',
                                color: '#000000',
                                alignSelf: 'flex-start',
                                marginBottom: '2px'
                              }}>
                                {format(day, 'dd MMM')}
                              </div>

                              {/* Main content centered */}
                              <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1
                              }}>
                                {block ? (
                                  <>
                                    <div style={{ 
                                      fontSize: '12px', 
                                      fontWeight: '800',
                                      textAlign: 'center',
                                      marginBottom: '4px'
                                    }}>
                                      BLOCKED
                                    </div>
                                    <div style={{ 
                                      fontSize: '10px', 
                                      textAlign: 'center',
                                      opacity: 0.95,
                                      lineHeight: '1.2'
                                    }}>
                                      {block.blockType?.replace('_', ' ')}
                                    </div>
                                  </>
                                ) : booking ? (
                                  <>
                                    <div style={{ 
                                      fontSize: '12px', 
                                      fontWeight: '800',
                                      textAlign: 'center',
                                      marginBottom: '4px'
                                    }}>
                                      BOOKED
                                    </div>
                                    <div style={{ 
                                      fontSize: '11px', 
                                      textAlign: 'center',
                                      opacity: 0.95,
                                      lineHeight: '1.2',
                                      marginBottom: '2px'
                                    }}>
                                      {booking.guestName?.split(' ')[0] || 'Guest'}
                                    </div>
                                    {/* Booking Engine Icon */}
                                    {booking.bookingPlatform === 'BOOKING_ENGINE' && (
                                      <div style={{ 
                                        fontSize: '12px', 
                                        marginTop: '2px',
                                        opacity: 0.9
                                      }} title="Booking Engine">
                                        <GlobeIcon size="1em" aria-hidden="true" />
                                      </div>
                                    )}
                                    <div style={{ 
                                      fontSize: '9px', 
                                      textAlign: 'center',
                                      opacity: 0.8
                                    }}>
                                      {booking.nights} night{booking.nights !== 1 ? 's' : ''}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div style={{ 
                                      fontSize: '12px', 
                                      fontWeight: '800',
                                      textAlign: 'center',
                                      marginBottom: '4px'
                                    }}>
                                      AVAILABLE
                                    </div>
                                    <div style={{ 
                                      fontSize: '9px', 
                                      textAlign: 'center',
                                      opacity: 0.9,
                                      marginTop: '2px'
                                    }}>
                                      ₹{room.roomType?.defaultRate || 0}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Updated Booking/Block Details Modal */}
      {showBookingModal && selectedBooking && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.98)' : 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: isMobile ? '0' : '16px'
          }}
          onClick={closeBookingModal}
        >
          <div
            style={{
              backgroundColor: '#E9DFCE',
              padding: isMobile ? '16px' : '30px',
              borderRadius: isMobile ? '0' : '16px',
              maxWidth: isMobile ? '100%' : '500px',
              width: isMobile ? '100%' : '90%',
              height: isMobile ? '100vh' : 'auto',
              maxHeight: isMobile ? '100vh' : '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              marginBottom: '25px',
              borderBottom: '2px solid #EEEBE1',
              paddingBottom: '15px',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <h3 style={{ margin: 0, color: '#24332A', fontSize: '24px', fontWeight: '800' }}>
                {selectedBooking.type === 'block' ? 'Block Details' : 'Booking Details'}
              </h3>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
                {/* Edit/Delete Block Buttons - Show only for blocks with proper permissions */}
                {(() => {
                  const isBlock = selectedBooking?.type === 'block';
                  
                  if (isBlock && canCreateReservations) {
                    return (
                      <>
                        <button
                          onClick={() => handleEditBlock(selectedBooking)}
                          style={{
                            backgroundColor: '#1A3E2A',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#163626'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1A3E2A'}
                        >
                          <span><PencilIcon size="1em" aria-hidden="true" /></span>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteBlock(selectedBooking)}
                          style={{
                            backgroundColor: '#A13F2F',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#8E3628'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#A13F2F'}
                        >
                          <span><Trash2Icon size="1em" aria-hidden="true" /></span>
                          Delete
                        </button>
                      </>
                    );
                  }
                  
                  return null;
                })()}

                {/* Edit Booking Button - Show only for bookings with proper permissions */}
                {(() => {
                  const isBooking = selectedBooking?.type === 'booking';
                  
                  if (!isBooking) return null;
                  
                  console.log('Modal Header Edit Button Permission Check:', {
                    isBooking: isBooking,
                    canCreateReservations: canCreateReservations,
                    shouldShow: isBooking && canCreateReservations
                  });
                  
                  if (canCreateReservations) {
                    return (
                      <button
                        onClick={() => {
                          console.log('Modal Header Edit Button clicked with selectedBooking:', selectedBooking);
                          openEditBookingModal(selectedBooking);
                        }}
                        style={{
                          backgroundColor: '#163626',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#122E20'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#163626'}
                      >
                        <span><PencilIcon size="1em" aria-hidden="true" /></span>
                        Edit
                      </button>
                    );
                  }
                  
                  return null;
                })()}
                
                <button
                  onClick={closeBookingModal}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '28px',
                    cursor: 'pointer',
                    color: '#5F6B61',
                    padding: '5px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ fontSize: '16px', lineHeight: '1.8' }}>
              {selectedBooking.type === 'block' ? (
                // Block Information
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '140px 1fr', 
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontWeight: '600', color: '#36433B' }}>Block Name:</div>
                  <div style={{ color: '#24332A', fontWeight: '600' }}>
                    {selectedBooking.name}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Block Type:</div>
                  <div style={{ color: '#24332A' }}>
                    {selectedBooking.blockType?.replace('_', ' ') || 'Custom'}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Location:</div>
                  <div style={{ color: '#24332A' }}>
                    Room {selectedBooking.room.roomNumber}
                    {selectedBooking.bedId && selectedBooking.bed && ` - Bed ${selectedBooking.bed.bedNumber}`}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Start Date:</div>
                  <div style={{ color: '#24332A' }}>
                    {(() => {
                      try {
                        const date = new Date(selectedBooking.startDate);
                        return isNaN(date.getTime()) ? 'Invalid Date' : format(date, 'EEEE, MMMM dd, yyyy');
                      } catch (error) {
                        return 'Invalid Date';
                      }
                    })()}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>End Date:</div>
                  <div style={{ color: '#24332A' }}>
                    {(() => {
                      try {
                        const date = new Date(selectedBooking.endDate);
                        return isNaN(date.getTime()) ? 'Invalid Date' : format(date, 'EEEE, MMMM dd, yyyy');
                      } catch (error) {
                        return 'Invalid Date';
                      }
                    })()}
                  </div>

                  {selectedBooking.description && (
                    <>
                      <div style={{ fontWeight: '600', color: '#36433B' }}>Description:</div>
                      <div style={{ color: '#24332A' }}>
                        {selectedBooking.description}
                      </div>
                    </>
                  )}

                  {selectedBooking.createdByUser && (
                    <>
                      <div style={{ fontWeight: '600', color: '#36433B' }}>Created By:</div>
                      <div style={{ color: '#24332A' }}>
                        {selectedBooking.createdByUser.name || selectedBooking.createdByUser.email}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                // Booking Information (existing code)
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '140px 1fr', 
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontWeight: '600', color: '#36433B' }}>Booking ID:</div>
                  <div style={{ color: '#24332A', fontFamily: 'monospace' }}>
                    {selectedBooking.bookingId?.slice(-8) || 'N/A'}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Guest Name:</div>
                  <div style={{ color: '#24332A', fontWeight: '600' }}>
                    {selectedBooking.guestName || 'Unknown Guest'}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Room:</div>
                  <div style={{ color: '#24332A' }}>
                    Room {selectedBooking.room.roomNumber}
                    {selectedBooking.bedNumber && ` - Bed ${selectedBooking.bedNumber}`}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Check-in:</div>
                  <div style={{ color: '#24332A' }}>
                    {(() => {
                      try {
                        const date = new Date(selectedBooking.startDate);
                        return isNaN(date.getTime()) ? 'Invalid Date' : format(date, 'EEEE, MMMM dd, yyyy');
                      } catch (error) {
                        return 'Invalid Date';
                      }
                    })()}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Check-out:</div>
                  <div style={{ color: '#24332A' }}>
                    {(() => {
                      try {
                        const date = new Date(selectedBooking.endDate);
                        return isNaN(date.getTime()) ? 'Invalid Date' : format(date, 'EEEE, MMMM dd, yyyy');
                      } catch (error) {
                        return 'Invalid Date';
                      }
                    })()}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Duration:</div>
                  <div style={{ color: '#24332A' }}>
                    {(() => {
                      try {
                        const checkIn = new Date(selectedBooking.startDate);
                        const checkOut = new Date(selectedBooking.endDate);
                        if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
                          return 'Invalid Duration';
                        }
                        const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                        return `${nights} night${nights !== 1 ? 's' : ''}`;
                      } catch (error) {
                        return 'Invalid Duration';
                      }
                    })()}
                  </div>

                  <div style={{ fontWeight: '600', color: '#36433B' }}>Status:</div>
                  <div>
                    <span style={{
                      backgroundColor: selectedBooking.status === 'CONFIRMED' ? '#1A3E2A' : 
                                     selectedBooking.status === 'PENDING' ? '#B68235' : 
                                     selectedBooking.status === 'CHECKEDIN' ? '#D48A7C' :
                                     selectedBooking.status === 'CHECKEDOUT' ? '#772D21' : '#5F6B61',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {selectedBooking.status}
                    </span>
                  </div>

                  {selectedBooking.totalPrice && (
                    <>
                      <div style={{ fontWeight: '600', color: '#36433B' }}>Total Price:</div>
                      <div style={{ 
                        color: '#163626', 
                        fontWeight: '700',
                        fontSize: '18px'
                      }}>
                        ₹{selectedBooking.totalPrice}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={{ 
                backgroundColor: '#F5F3EB',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '20px'
              }}>
                <h4 style={{ 
                  margin: '0 0 10px 0', 
                  color: '#36433B',
                  fontSize: '16px'
                }}>
                  Room Information
                </h4>
                <div style={{ fontSize: '14px', color: '#5F6B61' }}>
                  <div>Type: {selectedBooking.room.roomType?.name || 'Standard'}</div>
                  {selectedBooking.room.property && (
                    <div>Property: {selectedBooking.room.property.name}</div>
                  )}
                  {selectedBooking.room.roomType?.isShared && (
                    <div>Shared room with {selectedBooking.room.beds?.length || 0} beds</div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ 
              marginTop: '25px', 
              textAlign: 'center',
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {(() => {
                const isBooking = selectedBooking?.type === 'booking';
                
                console.log('Bottom Modal Edit Button Debug:', { 
                  selectedBookingType: selectedBooking?.type, 
                  canCreateReservations: canCreateReservations,
                  isBooking: isBooking,
                  shouldShowButton: isBooking && canCreateReservations,
                  selectedBooking: selectedBooking
                });
                
                if (isBooking && canCreateReservations) {
                
                console.log('� COMPLETE EDIT BUTTON DEBUG:', {
                  selectedBooking: selectedBooking,
                  selectedBookingId: selectedBooking?.id,
                  selectedBookingType: selectedBooking?.type,
                  isBooking: isBooking,
                  canCreateReservations: canCreateReservations,
                  selectedBooking: selectedBooking
                });

                  return (
                    <button
                      onClick={() => {
                        console.log('Bottom Modal Edit Button clicked with selectedBooking:', selectedBooking);
                        openEditBookingModal(selectedBooking);
                      }}
                      style={{
                        backgroundColor: '#163626',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <span><PencilIcon size="1em" aria-hidden="true" /></span>
                      Edit Booking
                    </button>
                  );
                }
                
                return null;
              })()}
              
              <button
                onClick={closeBookingModal}
                style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {(() => {
        console.log('EditBookingModal render check:', {
          showEditBookingModal,
          selectedBooking,
          selectedBookingType: selectedBooking?.type,
          hasSelectedBooking: !!selectedBooking,
          isBookingObject: selectedBooking && (selectedBooking.id || selectedBooking.bookingId) && !selectedBooking.type
        });
        
        // Check if selectedBooking is a booking object (has id/bookingId but no type property, or type === 'booking')
        const isBookingObject = selectedBooking && 
          ((selectedBooking.id || selectedBooking.bookingId) && (!selectedBooking.type || selectedBooking.type === 'booking'));
        
        return showEditBookingModal && selectedBooking && isBookingObject ? (
          <EditBookingModal
            booking={selectedBooking}
            onClose={closeEditBookingModal}
            onUpdate={closeEditBookingModal}
          />
        ) : null;
      })()}

      {/* Availability Block Manager */}
      {showBlockManager && (
        <AvailabilityBlockManager 
          onClose={() => {
            setShowBlockManager(false);
            setPrefilledBlockData(null); // Reset pre-filled data on close
          }} 
          onBlockCreated={loadRoomsAndBookings}
          initialData={prefilledBlockData} // Pass pre-filled data
        />
      )}

      {/* Staff Reservation Sidebar - Shown conditionally based on user permissions */}
      {canCreateReservations && (
        <StaffReservationSidebar 
          isOpen={showStaffReservationSidebar} 
          onClose={() => setShowStaffReservationSidebar(false)} 
          onReservationCreated={handleReservationCreated}
          user={user}
        />
      )}

      {/* Booking Action Menu - Appears when clicking on a booking */}
      {showBookingActionMenu && actionMenuBooking && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1500,
              backdropFilter: 'blur(2px)'
            }}
            onClick={closeBookingActionMenu}
          />
          
          {/* Action Menu */}
          <div
            style={{
              position: 'fixed',
              left: isMobile ? '12px' : Math.max(12, Math.min(actionMenuPosition.x - 150, window.innerWidth - 320)),
              top: isMobile ? 'auto' : Math.max(12, Math.min(actionMenuPosition.y + 10, window.innerHeight - 300)),
              right: isMobile ? '12px' : 'auto',
              bottom: isMobile ? '12px' : 'auto',
              backgroundColor: '#E9DFCE',
              borderRadius: '16px',
              padding: isMobile ? '16px' : '20px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
              zIndex: 1600,
              minWidth: isMobile ? 'auto' : '300px',
              width: isMobile ? 'auto' : '300px',
              maxWidth: isMobile ? 'calc(100vw - 24px)' : '360px',
              border: '1px solid #E3DFD3'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Menu Header */}
            <div style={{ 
              marginBottom: '20px',
              paddingBottom: '15px',
              borderBottom: '2px solid #EEEBE1'
            }}>
              <h4 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '18px', 
                fontWeight: '700',
                color: '#24332A',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                Booking Actions
              </h4>
              <div style={{ 
                fontSize: '14px', 
                color: '#5F6B61',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px'
              }}>
                <div><strong>Guest:</strong> {actionMenuBooking.guestName || 'Unknown Guest'}</div>
                <div><strong>Room:</strong> Room {actionMenuBooking.room?.roomNumber}{actionMenuBooking.bedNumber && ` - Bed ${actionMenuBooking.bedNumber}`}</div>
                <div><strong>Status:</strong> 
                  <span style={{
                    backgroundColor: actionMenuBooking.status === 'CONFIRMED' ? '#1A3E2A' : 
                                   actionMenuBooking.status === 'PENDING' ? '#B68235' : 
                                   actionMenuBooking.status === 'CHECKEDIN' ? '#D48A7C' :
                                   actionMenuBooking.status === 'CHECKEDOUT' ? '#772D21' : '#5F6B61',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    marginLeft: '6px'
                  }}>
                    {actionMenuBooking.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <button
                onClick={handleViewBookingDetails}
                style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '14px 20px',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(26, 62, 42, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#163626';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(26, 62, 42, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1A3E2A';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(26, 62, 42, 0.3)';
                }}
              >
                <span style={{ fontSize: '16px' }}><EyeIcon size="1em" aria-hidden="true" /></span>
                View Booking Details
              </button>

              {/* Edit Booking Button - Show only for authorized users */}
              {(() => {
                console.log('Action Menu Edit Button Permission Check:', {
                  canCreateReservations: canCreateReservations,
                  actionMenuBooking: actionMenuBooking
                });
                
                if (canCreateReservations) {
                  return (
                    <button
                      onClick={handleEditBookingFromMenu}
                      style={{
                        backgroundColor: '#163626',
                        color: 'white',
                        border: 'none',
                        padding: '14px 20px',
                        borderRadius: '10px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(22, 54, 38, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#122E20';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(22, 54, 38, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#163626';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(22, 54, 38, 0.3)';
                      }}
                    >
                      <span style={{ fontSize: '16px' }}><PencilIcon size="1em" aria-hidden="true" /></span>
                      Edit Booking
                    </button>
                  );
                }
                return null;
              })()}

              <button
                onClick={closeBookingActionMenu}
                style={{
                  backgroundColor: '#EEEBE1',
                  color: '#5F6B61',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#E3DFD3';
                  e.target.style.color = '#36433B';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#EEEBE1';
                  e.target.style.color = '#5F6B61';
                }}
              >
                <span style={{ fontSize: '14px' }}><XCircleIcon size="1em" aria-hidden="true" /></span>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* Edit Block Modal */}
      {showEditBlockModal && editingBlock && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.98)' : 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: isMobile ? '0' : '16px'
          }}
          onClick={closeEditBlockModal}
        >
          <div
            style={{
              backgroundColor: '#E9DFCE',
              padding: isMobile ? '16px' : '30px',
              borderRadius: isMobile ? '0' : '16px',
              maxWidth: isMobile ? '100%' : '600px',
              width: isMobile ? '100%' : '90%',
              height: isMobile ? '100vh' : 'auto',
              maxHeight: isMobile ? '100vh' : '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '25px',
              borderBottom: '2px solid #EEEBE1',
              paddingBottom: '15px'
            }}>
              <h3 style={{ margin: 0, color: '#24332A', fontSize: '24px', fontWeight: '800' }}>
                Edit Block
              </h3>
              
              <button
                onClick={closeEditBlockModal}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#5F6B61',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>

            <EditBlockForm
              block={editingBlock}
              rooms={rooms}
              onSave={handleUpdateBlock}
              onCancel={closeEditBlockModal}
              isMobile={isMobile}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Edit Block Form Component
function EditBlockForm({ block, rooms, onSave, onCancel, isMobile = false }) {
  // Auto-fill roomId and bedId from the clicked block's room/bed data
  const initialRoomId = block.roomId || block.room?.id || '';
  const initialBedId = block.bedId || block.bed?.id || '';
  
  const [formData, setFormData] = useState({
    name: block.name || '',
    description: block.description || '',
    startDate: block.startDate?.split('T')[0] || '',
    endDate: block.endDate?.split('T')[0] || '',
    blockType: block.blockType || 'MAINTENANCE',
    roomId: initialRoomId,
    bedId: initialBedId
  });
  const [loading, setLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [filteredBeds, setFilteredBeds] = useState([]);

  const BLOCK_TYPES = {
    MAINTENANCE: { label: 'Maintenance', color: '#B68235' },
    SUITE_BLOCK: { label: 'Suite Block', color: '#A9613C' },
    DEEP_CLEANING: { label: 'Deep Cleaning', color: '#1A3E2A' },
    RENOVATION: { label: 'Renovation', color: '#A13F2F' },
    SEASONAL_CLOSE: { label: 'Seasonal Close', color: '#5F6B61' },
    STAFF_USE: { label: 'Staff Use', color: '#1A3E2A' },
    CUSTOM: { label: 'Custom', color: '#5F6B61' }
  };

  // Log auto-filled data on mount
  useEffect(() => {
    console.log('Edit Block Form initialized with:', {
      blockData: block,
      autoFilledRoomId: initialRoomId,
      autoFilledBedId: initialBedId,
      roomFromBlock: block.room,
      bedFromBlock: block.bed
    });
  }, []);

  useEffect(() => {
    if (formData.roomId) {
      const room = rooms.find(r => r.id === formData.roomId);
      setSelectedRoom(room);
      setFilteredBeds(room?.beds || []);
      
      console.log('Room selected:', {
        roomId: formData.roomId,
        room: room,
        availableBeds: room?.beds || []
      });
    } else {
      setSelectedRoom(null);
      setFilteredBeds([]);
    }
  }, [formData.roomId, rooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Block name is required');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      toast.error('Start and end dates are required');
      return;
    }

    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      toast.error('End date must be after start date');
      return;
    }

    if (!formData.roomId) {
      toast.error('Please select a room');
      return;
    }

    setLoading(true);
    try {
      await onSave(block.id, formData);
    } catch (error) {
      console.error('Error updating block:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Block Name */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
            Block Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Winter Maintenance 2026"
            required
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '2px solid #E3DFD3',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Block Type */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
            Block Type *
          </label>
          <select
            value={formData.blockType}
            onChange={(e) => setFormData({ ...formData, blockType: e.target.value })}
            required
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '2px solid #E3DFD3',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          >
            {Object.entries(BLOCK_TYPES).map(([key, { label, icon }]) => (
              <option key={key} value={key}>
                {icon} {label}
              </option>
            ))}
          </select>
        </div>

        {/* Room Selection */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
            Room *
            {block.room && (
              <span style={{ 
                marginLeft: '8px', 
                fontSize: '12px', 
                color: '#1A3E2A', 
                fontWeight: '600',
                backgroundColor: '#E1E9E2',
                padding: '2px 8px',
                borderRadius: '4px'
              }}>
                ✓ Auto-filled from calendar
              </span>
            )}
          </label>
          <select
            value={formData.roomId}
            onChange={(e) => setFormData({ ...formData, roomId: e.target.value, bedId: '' })}
            required
            style={{
              width: '100%',
              padding: '10px 14px',
              border: block.room ? '2px solid #1A3E2A' : '2px solid #E3DFD3',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: block.room ? '#F0F3EE' : 'white'
            }}
          >
            <option value="">Select a room</option>
            {rooms.map(room => (
              <option key={room.id} value={room.id}>
                Room {room.roomNumber} - {room.roomType?.name || 'Standard'}
              </option>
            ))}
          </select>
        </div>

        {/* Bed Selection (Optional) */}
        {filteredBeds.length > 0 && (
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
              Bed (Optional - Leave empty to block entire room)
              {block.bed && formData.bedId && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '12px', 
                  color: '#1A3E2A', 
                  fontWeight: '600',
                  backgroundColor: '#E1E9E2',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }}>
                  ✓ Auto-filled from calendar
                </span>
              )}
            </label>
            <select
              value={formData.bedId}
              onChange={(e) => setFormData({ ...formData, bedId: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: (block.bed && formData.bedId) ? '2px solid #1A3E2A' : '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: (block.bed && formData.bedId) ? '#F0F3EE' : 'white'
              }}
            >
              <option value="">Entire Room</option>
              {filteredBeds.map(bed => (
                <option key={bed.id} value={bed.id}>
                  Bed {bed.bedNumber} - {bed.bedType}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Date Range */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
              Start Date *
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
              End Date *
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '2px solid #E3DFD3',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#36433B' }}>
            Description (Optional)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Add any notes or details about this block..."
            rows={3}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '2px solid #E3DFD3',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
              backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '14px 20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.backgroundColor = '#163626';
            }}
            onMouseLeave={(e) => {
              if (!loading) e.target.style.backgroundColor = '#1A3E2A';
            }}
          >
            {loading ? 'Updating...' : 'Update Block'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1,
              backgroundColor: '#EEEBE1',
              color: '#5F6B61',
              border: 'none',
              padding: '14px 20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#E3DFD3';
                e.target.style.color = '#36433B';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#EEEBE1';
                e.target.style.color = '#5F6B61';
              }
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

export default AvailabilityCalendar;
